import React from "react";
import { motion } from "framer-motion";
import Heading from "../../atoms/Heading/Heading";

interface FrameworkStep {
  key: number;
  stepLabel: string;
  title: string;
  body: string;
  techStackTags: string[];
}

interface ThreeStepFrameworkSectionData {
  title?: string;
  subTitle?: string;
  text?: string;
  steps?: FrameworkStep[];
}

interface ThreeStepFrameworkSectionProps {
  data?: ThreeStepFrameworkSectionData;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const ThreeStepFrameworkSection = ({ data }: ThreeStepFrameworkSectionProps) => {
  if (!data) return null;

  return (
    <section
      id="framework"
      className="relative overflow-hidden bg-[#171717] pt-8 pb-12 sm:pt-10 sm:pb-14 md:pt-12 md:pb-16 lg:pt-14 lg:pb-20"
    >

      <motion.div
        className="relative mx-auto w-full max-w-[1140px] px-4 sm:px-8 md:px-12 lg:px-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <Heading
            title={data.title}
            subTitle={data.subTitle}
            text={data.text}
            className="!px-0"
          />
        </motion.div>

        <div className="relative mx-auto mt-6 max-w-5xl sm:mt-10 md:mt-12">
         <motion.div
  className="absolute left-[40px] top-0 bottom-0 w-px bg-[#4F8EF7]/55 sm:left-[20px] md:left-[24px]"
  initial={{ scaleY: 0 }}
  whileInView={{ scaleY: 1 }}
  viewport={{ once: true, amount: 0.2 }}
  transition={{ duration: 1.1, ease: "easeOut" }}
  style={{ transformOrigin: "top" }}
/>

          <div className="space-y-4 sm:space-y-6 md:space-y-10">
            {data.steps?.map((step, index) => (
              <motion.article
                key={step.key}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                className="relative pl-16 md:pl-20"
              >
                <div className="absolute left-[36px] top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-[#4F8EF7]  sm:left-[16px] sm:h-3 sm:w-3  md:left-[19px] " />
                <div className="absolute left-[-30px] top-1/2 -translate-y-1/2 w-16 text-right text-[35px] font-semibold text-white sm:left-[-80px] sm:w-20 sm:text-[35px] md:left-[-96px] md:w-24 md:text-[35px] lg:left-[-90px] lg:text-[40px] xl:left-[-96px] xl:text-[40px] 2xl:left-[-104px] 2xl:text-[44px]">
  {String(index + 1).padStart(2, "0")}
</div>

                <div className="relative overflow-hidden rounded-xl border border-[#2E2E2E] bg-[#121212] px-3 py-3 sm:rounded-2xl sm:px-5 sm:py-5 md:px-6 md:py-7">
                  <div className="relative z-10">
                    <h3 className="mt-1.5 text-base font-semibold leading-tight text-white sm:mt-2 sm:text-2xl md:text-3xl">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[12px] leading-relaxed text-[#C8C8C8] sm:mt-3 sm:text-[15px] md:mt-4 md:text-lg">
                      {step.body}
                    </p>

                    {/* <div className="mt-3 flex flex-wrap gap-1 sm:mt-5 sm:gap-2">
                      {step.techStackTags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-[#3A3A3A] bg-[#1D1D1D] px-2 py-0.5 text-[9px] font-medium tracking-[0.02em] text-[#E5E5E5] sm:px-3 sm:py-1 sm:text-xs md:text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div> */}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ThreeStepFrameworkSection;
